import { useCustomerBookingCtx } from '@/app/(app)/customers/[id]/customer-booking-provider';
import { TimeInUTC } from '@/components/atoms/datetime';
import { EmployeeAvatar } from '@/components/molecules/employee-avatar';
import { ApiSchemas } from '@/components/providers/v1-api';
import { useEmployeeById } from '@/components/providers/v1-office-hooks';
import { formatMetersAsMiles, formatTravelTime } from '@/lib/format';

type ApiAvailability = ApiSchemas['Availability'];

interface AvailabilityTimeWindow {
  windowStartTime: string;
  windowEndTime: string;
  bestSlot: ApiAvailability['slots'][number];
  slots: ApiAvailability['slots'];
}

export interface CustomerAvailability {
  routeId: string;
  employeeId: string | null;
  timeWindows: AvailabilityTimeWindow[];
}

interface AvailabilityCardProps {
  availability: CustomerAvailability;
}

export function AvailabilityCard({ availability }: AvailabilityCardProps) {
  const { setSelected } = useCustomerBookingCtx();
  const employee = useEmployeeById(availability.employeeId);

  return (
    <div className='w-full space-y-2 rounded-3xl p-3 shadow-md'>
      <div className='flex w-full items-center gap-2'>
        <EmployeeAvatar employeeId={availability.employeeId} />
        <div className='w-full space-y-1.5'>
          <div className='text-sm leading-none'>
            {employee?.first_name} {employee?.last_name}
          </div>
        </div>
      </div>
      <div className='flex w-full flex-col gap-2'>
        {availability.timeWindows.map((timeWindow) => (
          <button
            key={timeWindow.windowStartTime}
            className='flex w-full items-center gap-2 rounded-lg p-2 shadow-sm transition-colors hover:bg-muted/50'
            onClick={() =>
              setSelected({
                routeId: availability.routeId,
                employeeId: availability.employeeId,
                windowStartTime: timeWindow.windowStartTime,
                windowEndTime: timeWindow.windowEndTime,
                slot: timeWindow.bestSlot,
                slots: timeWindow.slots,
              })
            }
          >
            <div className='text-sm font-medium'>
              <TimeInUTC value={timeWindow.windowStartTime} format='h:mm a' /> -{' '}
              <TimeInUTC value={timeWindow.windowEndTime} format='h:mm a' />
            </div>
            <div className='ml-auto inline-flex items-center gap-2 text-sm text-muted-foreground'>
              <TravelDistance value={timeWindow.bestSlot.travel_distance_meters} />
              <TravelTime value={timeWindow.bestSlot.travel_time_seconds} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function TravelDistance({ value }: { value?: number | null | undefined }) {
  if (!isFiniteNumber(value)) return null;
  return <span>+{formatMetersAsMiles(Math.max(0, value))} miles</span>;
}

function TravelTime({ value }: { value?: number | null | undefined }) {
  if (!isFiniteNumber(value)) return null;
  return <span>+{formatTravelTime(Math.max(0, value))}</span>;
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value);
}
